import apiClient from '../server';

export const fetchPublications = async (page, limit, publicationType, isPublished, search) => {
  try {
    let url = `/publications/all?pagination=true&page=${page}&limit=${limit}&optimized=true`;
    if (publicationType) {
      url += `&publicationType=${publicationType}`;
    }
    if (isPublished !== undefined) {
      url += `&isPublished=${isPublished}`;
    }
    if (search !== undefined && search !== null && search !== '') {
      url += `&search=${encodeURIComponent(search)}`;
    }

    console.log('📡 [Publications API] Fetching with URL:', url);
    console.log('📡 [Publications API] Search parameter:', search);
    
    const response = await apiClient.get(url);
    console.log('📡 [Publications API] Response:', response.data);
    return response.data;
  } catch (error) {
    console.error('Error fetching publications:', error);
    throw error;
  }
};

export const GetPublicationById = async (id) => {
  try {
    const response = await apiClient.get(`/publications/get/${id}?optimized=true`);
    
    // apiClient (axios) automatically handles JSON parsing and puts data in response.data
    console.log('Publication detail response:', response.data);
    return response.data.data || response.data;
  } catch (error) {
    console.error("Error fetching publication:", error);
    throw error;
  }
};

export const updatePublication = async(id,publication)=>{
  try{
    const response=await apiClient.patch(`/publications/update/${id}?optimized=true`, publication);
    return response;
  }catch(error){
    console.error('error updating publication : ',error);
    throw error;
  }
}

// Backoffice update function that bypasses ownership restrictions
export const updatePublicationBackoffice = async(id,publication)=>{
  try{
    const response=await apiClient.patch(`/backoffice/publications/update/${id}`, publication);
    return response;
  }catch(error){
    console.error('error updating publication via backoffice : ',error);
    throw error;
  }
}

// Direct entity update functions (bypass ownership restrictions)
export const updateEventDirect = async(id, eventData)=>{
  try{
    const response=await apiClient.patch(`/backoffice/events/update/${id}`, eventData);
    return response;
  }catch(error){
    console.error('error updating event directly : ',error);
    throw error;
  }
}

export const updateTrackDirect = async(id, trackData)=>{
  try{
    const response=await apiClient.patch(`/tracks/backoffice/update/${id}`, trackData);
    return response;
  }catch(error){
    console.error('error updating track directly : ',error);
    throw error;
  }
}

export const updateArticleDirect = async(id, articleData)=>{
  try{
    const response=await apiClient.patch(`/backoffice/articles/update/${id}`, articleData);
    return response;
  }catch(error){
    console.error('error updating article directly : ',error);
    throw error;
  }
}

export const deletePublication=async(id)=>{
  try{
    const response=await apiClient.delete(`/publications/delete/${id}?optimized=true`);
    return response.data;
  }catch(error){
    console.error("error deleting publication", error);
    throw error;
  }
}

// Enhanced entity deletion functions
export const deleteEventDirect = async (eventId) => {
  try {
    console.log('🗑️ [Publication Server] Deleting event directly:', eventId);
    const response = await apiClient.delete(`/backoffice/events/delete/${eventId}`);
    console.log('🗑️ [Publication Server] Event deleted:', response.data);
    return response.data;
  } catch (error) {
    console.error('🗑️ [Publication Server] Error deleting event:', error);
    throw error;
  }
};

export const deleteTrackDirect = async (trackId) => {
  try {
    console.log('🗑️ [Publication Server] Deleting track directly:', trackId);
    const response = await apiClient.delete(`/backoffice/tracks/delete/${trackId}`);
    console.log('🗑️ [Publication Server] Track deleted:', response.data);
    return response.data;
  } catch (error) {
    console.error('🗑️ [Publication Server] Error deleting track:', error);
    throw error;
  }
};

export const deleteArticleDirect = async (articleId) => {
  try {
    console.log('🗑️ [Publication Server] Deleting article directly:', articleId);
    const response = await apiClient.delete(`/backoffice/articles/delete/${articleId}`);
    console.log('🗑️ [Publication Server] Article deleted:', response.data);
    return response.data;
  } catch (error) {
    console.error('🗑️ [Publication Server] Error deleting article:', error);
    throw error;
  }
};

// Enhanced deletePublication function that works with optimized structure
export const deletePublicationOptimized = async (publicationId, publicationType) => {
  try {
    console.log('🗑️ [Publication Server] Deleting publication with optimized approach:', { publicationId, publicationType });
    
    let response;
    
    // Use direct entity deletion based on publication type
    switch (publicationType?.toUpperCase()) {
      case 'EVENT':
        response = await deleteEventDirect(publicationId);
        break;
      case 'TRACK':
        response = await deleteTrackDirect(publicationId);
        break;
      case 'ARTICLE':
        response = await deleteArticleDirect(publicationId);
        break;
      default:
        // Fallback to original publication deletion
        console.log('🗑️ [Publication Server] Using fallback publication deletion for type:', publicationType);
        response = await apiClient.delete(`/publications/delete/${publicationId}?optimized=true`);
        return response.data;
    }
    
    console.log('🗑️ [Publication Server] Publication deleted successfully:', response);
    return response;
  } catch (error) {
    console.error('🗑️ [Publication Server] Error deleting publication:', error);
    throw error;
  }
};

export const createPublication = async (publicationData) => {
  try {
    const response = await apiClient.post('/publications/create', publicationData);
    return response;
  } catch (error) {
    console.error('error creating publication : ', error);
    throw error;
  }
};

// Backoffice create event function
export const createEventBackoffice = async (eventData) => {
  try {
    const response = await apiClient.post('/backoffice/events/create', eventData);
    return response;
  } catch (error) {
    console.error('error creating event via backoffice : ', error);
    throw error;
  }
};

// Fetch publications for a profile with query params
export const fetchProfilePublications = async (profileId, options = {}) => {
  try {
    let url = `/publications/profile/${profileId}`;
    const params = [];
    params.push(`optimized=true`); // Add optimized parameter
    if (options.publicationType) params.push(`publicationType=${options.publicationType}`);
    if (options.pagination !== undefined) params.push(`pagination=${options.pagination}`);
    if (options.limit !== undefined) params.push(`limit=${options.limit}`);
    if (options.page !== undefined) params.push(`page=${options.page}`);
    if (options.profileType) params.push(`profileType=${options.profileType}`);
    if (options.upComing !== undefined) params.push(`upComing=${options.upComing}`);
    if (options.pastEvents !== undefined) params.push(`pastEvents=${options.pastEvents}`);
    if (options.todayEvents !== undefined) params.push(`todayEvents=${options.todayEvents}`);
    if (options.isPrivate !== undefined) params.push(`isPrivate=${options.isPrivate}`);
    if (options.isPublished !== undefined) params.push(`isPublished=${options.isPublished}`);
    if (params.length > 0) {
      url += `?${params.join('&')}`;
    }
    console.log('🎵 [Publication Server] Final URL:', url, 'Options:', options);
    const response = await apiClient.get(url);
    console.log('🎵 [Publication Server] Response:', response);
    return response.data;
  } catch (error) {
    console.error('Error fetching profile publications:', error);
    throw error;
  }
};

// Fetch events for calendar view
export const fetchEventsForCalendar = async (options = {}) => {
  try {
    // Build query parameters for the discovery endpoint
    const params = new URLSearchParams();
    params.append('pagination', 'true');
    params.append('limit', '100');
    params.append('page', '1');
    params.append('publicationType', 'Event');
    params.append('condition[isPublished]', 'true');
    params.append('condition[upcoming]', 'true');
    
    // Add optional filters
    if (options.city) {
      params.append('filterLocation', options.city);
    }
    if (options.country) {
      // Note: Country filtering not supported by API, using city filtering instead
      params.append('country', options.country);
    }
    if (options.startDate) {
      // Convert ISO date to YYYY-MM-DD format
      const startDate = new Date(options.startDate).toISOString().split('T')[0];
      params.append('filter[startDate]', startDate);
    }
    if (options.endDate) {
      // Convert ISO date to YYYY-MM-DD format
      const endDate = new Date(options.endDate).toISOString().split('T')[0];
      params.append('filter[endDate]', endDate);
    }
    if (options.search) {
      params.append('search', options.search);
    }

    const url = `/publications/discovery?${params.toString()}`;
    const response = await apiClient.get(url);
    console.log('Calendar Events API Response:', response.data);
    return response.data;
  } catch (error) {
    console.error('Error fetching calendar events:', error);
    throw error;
  }
};